import { ExternalLink, GraduationCap, Mail, Copy } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Professional NHS e-LfH Care Certificate training card — shared by worker and admin views.
 */
export default function CareCertificateTrainingCard({
  programme,
  resources = [],
  standardTitle,
  standardNumber,
  compact = false,
  onSendLink,
  sendingLink = false,
  showSendLink = false,
  showResourceLinks = true,
}) {
  if (!programme?.primary_url && !resources?.length) return null;

  const primaryResource = resources.find((r) => r.primary) || resources[0];
  const primaryUrl = primaryResource?.url || programme?.primary_url;
  const isStandard16 = Number(standardNumber) === 16;

  const handleCopy = async () => {
    if (!primaryUrl) return;
    try {
      await navigator.clipboard.writeText(primaryUrl);
      toast.success('Training link copied');
    } catch {
      toast.error('Could not copy link');
    }
  };

  const headline = standardNumber && standardTitle
    ? `Standard ${standardNumber} — ${standardTitle}`
    : programme?.title || 'NHS Care Certificate training';

  return (
    <div
      className={`rounded-xl border border-[#0F5C5E]/20 bg-gradient-to-br from-[#D9F1F4]/40 via-white to-white shadow-sm ${
        compact ? 'p-4' : 'p-5'
      }`}
      data-testid="care-certificate-training-card"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#0F5C5E]/10">
          <GraduationCap className="h-5 w-5 text-[#0F5C5E]" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#0F5C5E]/80">
            {isStandard16 ? 'Required training — simple 3-step path' : 'NHS e-LfH — official learning'}
          </p>
          <h3 className="mt-1 font-heading text-base font-bold text-[#17324D]">{headline}</h3>
          <p className="mt-1.5 text-sm text-slate-600">
            {isStandard16
              ? 'Start with the free NHS e-learning. Osabea arranges the live Tier 2 session for care/support roles.'
              : (programme?.subtitle || programme?.registration_note)}
          </p>
        </div>
      </div>

      <div className={`mt-4 flex flex-wrap gap-2 ${compact ? '' : 'sm:gap-3'}`}>
        {showResourceLinks && primaryUrl && (
          <a
            href={primaryUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-lg bg-[#0F5C5E] px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-[#0c4a4c]"
            data-testid="care-cert-open-elfh-btn"
          >
            <ExternalLink className="h-4 w-4" />
            {isStandard16 ? 'Start free Part 1' : 'Open NHS learning'}
          </a>
        )}
        {showResourceLinks && <button
          type="button"
          onClick={handleCopy}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          <Copy className="h-4 w-4" />
          Copy link
        </button>}
        {showSendLink && onSendLink && (
          <button
            type="button"
            onClick={onSendLink}
            disabled={sendingLink}
            className="inline-flex items-center gap-2 rounded-lg border border-[#0F5C5E]/30 bg-white px-3 py-2 text-sm font-medium text-[#0F5C5E] hover:bg-[#D9F1F4]/30 disabled:opacity-60"
            data-testid="care-cert-send-link-btn"
          >
            <Mail className="h-4 w-4" />
            {sendingLink ? 'Sending…' : 'Send link to worker'}
          </button>
        )}
      </div>

      {showResourceLinks && !compact && resources.length > 1 && (
        <ul className="mt-4 space-y-2 border-t border-slate-100 pt-4">
          {resources.filter((r) => !r.primary).map((resource) => (
            <li key={`${resource.url}-${resource.label}`}>
              <a
                href={resource.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-start justify-between gap-2 rounded-lg border border-slate-100 bg-slate-50/80 px-3 py-2 text-sm hover:border-[#0F5C5E]/20 hover:bg-white"
              >
                <span>
                  <span className="font-medium text-slate-800 group-hover:text-[#0F5C5E]">{resource.label}</span>
                  {resource.provider && (
                    <span className="mt-0.5 block text-xs text-slate-500">{resource.provider}</span>
                  )}
                  {resource.description && (
                    <span className="mt-1 block text-xs leading-5 text-slate-500">{resource.description}</span>
                  )}
                </span>
                <ExternalLink className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
              </a>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-xs leading-5 text-slate-600">
        {isStandard16 ? (
          <p>
            <span className="font-semibold text-slate-700">What happens next:</span>{' '}
            1) finish Part 1 online; 2) Osabea arranges the Tier 2 one-day face-to-face session for care/support roles;
            3) CareOps records the evidence and assessor sign-off. You should not need to search for the live course yourself.
          </p>
        ) : (
          <p>
            NHS learning supports the knowledge part of the Care Certificate. Your employer/assessor still confirms
            workplace competence and final sign-off. Mandatory training and renewals are tracked separately.
          </p>
        )}
      </div>
    </div>
  );
}
